import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { CustomerAuthProvider } from "@/lib/customerAuth";
import { DivisionsVisibilityProvider } from "@/lib/divisionsVisibility";
import { ConsentProvider } from "@/lib/consent";
import { FeaturesProvider, useFeatures } from "@/lib/features";
import { MediaProvider } from "@/lib/media";
import { isShopHost, isMainProductionHost, shopUrl } from "@/lib/hostname";
import { Header, Footer, Noise } from "@/components/Layout";
import CookieBanner from "@/components/CookieBanner";
import HomePage from "@/pages/Home";
import DivisionPage from "@/pages/Division";
import LaserPage from "@/pages/Laser";
import QuotePage, { PaymentSuccess, PaymentCancel } from "@/pages/Quote";
import { AdminLogin, AdminDashboard } from "@/pages/Admin";
import AssistenzaPage from "@/pages/Assistenza";
import TicketPage from "@/pages/Ticket";
import { CustomerLogin, CustomerRegister, CustomerMagicVerify } from "@/pages/CustomerAuth";
import { BlogList, BlogPost } from "@/pages/Blog";
import CustomerArea from "@/pages/CustomerArea";
import ContattiPage from "@/pages/Contatti";
import PrivacyPage from "@/pages/Privacy";
import CookiePolicyPage from "@/pages/CookiePolicy";
import TerminiPage from "@/pages/Termini";
import ReportPage from "@/pages/Rapporto";
import { ShopHome, ShopProduct, ShopCart, ShopCheckout, ShopOrder } from "@/pages/Shop";
import ShopPolicy from "@/pages/ShopPolicy";
import { Navigate } from "react-router-dom";
import { CartProvider } from "@/lib/cart";
import { HelmetProvider } from "react-helmet-async";
import Seo, { localBusinessJsonLd } from "@/lib/Seo";
import { useConsent } from "@/lib/consent";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import "@/App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const { prefs } = useConsent();
  useGoogleAnalytics(Boolean(prefs?.analytics));
  return null;
}

function FeatureGate({ flag, children }) {
  const { features, loaded } = useFeatures();
  if (!loaded) return null; // avoid flash before flags load
  if (features[flag] === false) return <Navigate to="/" replace />;
  return children;
}

/**
 * Hostname-aware guards:
 * - On shop.gefion-evolution.it, non-shop URLs (e.g. "/") redirect to "/shop".
 * - On the corporate domain, /shop/* redirects to the shop subdomain.
 */
function ShopHostRedirectToShop() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.pathname && !loc.pathname.startsWith("/shop")) {
      window.location.replace("/shop" + (loc.pathname === "/" ? "" : loc.pathname) + (loc.search || ""));
    }
  }, [loc.pathname, loc.search]);
  return null;
}

function CorporateHostRedirectShopToSubdomain({ children }) {
  const loc = useLocation();
  // On the corporate production domain, redirect /shop/* to shop.gefion-evolution.it.
  // In non-production (preview/local) we keep the shop reachable at /shop for QA.
  if (isMainProductionHost() && loc.pathname.startsWith("/shop")) {
    if (typeof window !== "undefined") {
      window.location.replace(shopUrl(loc.pathname + (loc.search || "")));
    }
    return null;
  }
  return children;
}

function LenisProvider({ children }) {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return children;
}

function Shell({ children }) {
  return (
    <>
      <Noise />
      <Header />
      <main className="min-h-screen">{children}</main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CustomerAuthProvider>
        <DivisionsVisibilityProvider>
        <FeaturesProvider>
        <MediaProvider>
        <CartProvider>
        <ConsentProvider>
          <Seo jsonLd={localBusinessJsonLd} />
        <LenisProvider>
          <ScrollToTop />
          <AnalyticsTracker />
          {isShopHost() && <ShopHostRedirectToShop />}
          <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "#0a0a0a", border: "1px solid #1a1a1a", color: "#fff", fontFamily: "JetBrains Mono, monospace", fontSize: "12px" } }} />
          <Routes>
            {/* Public site */}
            <Route path="/" element={<Shell><HomePage /></Shell>} />
            <Route path="/divisione/:id" element={<Shell><DivisionPage /></Shell>} />
            <Route path="/taglio-laser" element={<Shell><LaserPage /></Shell>} />
            <Route path="/preventivo/:id" element={<Shell><QuotePage /></Shell>} />
            <Route path="/pagamento/successo" element={<Shell><PaymentSuccess /></Shell>} />
            <Route path="/pagamento/annullato" element={<Shell><PaymentCancel /></Shell>} />
            <Route path="/assistenza" element={<Shell><AssistenzaPage /></Shell>} />
            <Route path="/ticket" element={<Shell><TicketPage /></Shell>} />
            <Route path="/contatti" element={<Shell><ContattiPage /></Shell>} />
            <Route path="/privacy" element={<Shell><PrivacyPage /></Shell>} />
            <Route path="/cookie-policy" element={<Shell><CookiePolicyPage /></Shell>} />
            <Route path="/termini" element={<Shell><TerminiPage /></Shell>} />
            <Route path="/rapporto/:id" element={<Shell><ReportPage /></Shell>} />
            {/* Blog / News */}
            <Route path="/blog" element={<FeatureGate flag="blog_enabled"><Shell><BlogList /></Shell></FeatureGate>} />
            <Route path="/blog/:slug" element={<FeatureGate flag="blog_enabled"><Shell><BlogPost /></Shell></FeatureGate>} />
            {/* Shop (own layout, no main shell). On the corporate production
                domain these routes redirect to shop.gefion-evolution.it. */}
            <Route path="/shop" element={<CorporateHostRedirectShopToSubdomain><FeatureGate flag="shop_enabled"><ShopHome /></FeatureGate></CorporateHostRedirectShopToSubdomain>} />
            <Route path="/shop/spedizioni-resi" element={<CorporateHostRedirectShopToSubdomain><FeatureGate flag="shop_enabled"><ShopPolicy /></FeatureGate></CorporateHostRedirectShopToSubdomain>} />
            <Route path="/shop/carrello" element={<CorporateHostRedirectShopToSubdomain><FeatureGate flag="shop_enabled"><ShopCart /></FeatureGate></CorporateHostRedirectShopToSubdomain>} />
            <Route path="/shop/checkout" element={<CorporateHostRedirectShopToSubdomain><FeatureGate flag="shop_enabled"><ShopCheckout /></FeatureGate></CorporateHostRedirectShopToSubdomain>} />
            <Route path="/shop/ordine/:id" element={<CorporateHostRedirectShopToSubdomain><FeatureGate flag="shop_enabled"><ShopOrder /></FeatureGate></CorporateHostRedirectShopToSubdomain>} />
            <Route path="/shop/:slug" element={<CorporateHostRedirectShopToSubdomain><FeatureGate flag="shop_enabled"><ShopProduct /></FeatureGate></CorporateHostRedirectShopToSubdomain>} />
            {/* Customer area (own auth) */}
            <Route path="/area/login" element={<CustomerLogin />} />
            <Route path="/area/verifica" element={<CustomerMagicVerify />} />
            <Route path="/area/registrati" element={<Shell><CustomerRegister /></Shell>} />
            <Route path="/area" element={<Shell><CustomerArea /></Shell>} />
            {/* Admin (no shell — has own layout) */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
          <CookieBanner />
        </LenisProvider>
        </ConsentProvider>
        </CartProvider>
        </MediaProvider>
        </FeaturesProvider>
        </DivisionsVisibilityProvider>
        </CustomerAuthProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}
